<script setup lang="ts">
import type { Game, GameClickEventEmit, GameListProps } from '@game/types'
import { vIntersectionObserver } from '@vueuse/components'
import { computed, reactive, ref, whenever, useGameAssets, useDebounceFn, useTimeoutFn, onBeforeUnmount } from '#imports'

const { isVideoTile } = useGameAssets()

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    hasSidebar: false,
    videoEnabled: true,
    skeletons: 5,
})

const emit = defineEmits<GameClickEventEmit>()

const { trackEvent } = useTracking()

// Split games into rows depending on the inlineRows prop
const gameListRows = computed(() => {
    if (!props.games?.length) return []

    if (props.inlineRows && props.inlineRows > 1) {
        const rows = props.inlineRows
        const rowsArray: Game[][] = Array.from({ length: rows }, () => [])

        props.games.forEach((item, index) => {
            rowsArray[index % rows]!.push(item)
        })

        return rowsArray
    }

    return [props.games]
})

const skeletonRows = computed(() => props.inlineRows || 1)

const gameListElement = ref<HTMLDivElement[]>()
const disablePrev = ref(true)
const disableNext = ref(true)
const scrollLength = ref(718) // Default scroll length set at first real desktop breakpoint ($screen-lg)

let pendingScrollStops: (() => void)[] = []
onBeforeUnmount(() => pendingScrollStops.forEach(stop => stop()))

const scrollGameList = (next = true) => {
    pendingScrollStops.forEach(stop => stop())
    pendingScrollStops = []

    if (!gameListElement.value?.length) return
    let combinedScrollMax = 0

    gameListElement.value.forEach((element, index) => {
        const { scrollWidth, clientWidth, scrollLeft } = element
        const increment = next ? scrollLength.value : scrollLength.value * -1
        const scrollMax = scrollWidth - clientWidth
        const newScrollLeft = scrollLeft + increment < 0 ? 0 : scrollLeft + increment

        if (scrollMax > combinedScrollMax) {
            combinedScrollMax = scrollMax
        }

        const { stop } = useTimeoutFn(() => {
            element.scrollTo({
                left: scrollMax < newScrollLeft ? scrollMax : newScrollLeft,
                behavior: 'smooth',
            })

            if (combinedScrollMax <= newScrollLeft) {
                disableNext.value = true
            }
            else {
                disableNext.value = false
            }

            if (newScrollLeft <= 0) {
                disablePrev.value = true
            }
            else {
                disablePrev.value = false
            }
        }, index * 120)
        pendingScrollStops.push(stop)
    })
}

if (import.meta.client) {
    whenever(gameListElement, () => {
        if (!gameListElement.value?.length) return

        gameListElement.value.forEach((element) => {
            const scrollMax = element.scrollWidth - element.clientWidth
            const gametileWidth = element.querySelector('.game-tile')?.clientWidth

            if (gametileWidth) {
                const gametilesVisible = Math.floor(element.clientWidth / (gametileWidth + 10)) // 10px is the gap between tiles
                scrollLength.value = gametilesVisible * (gametileWidth + 10)
            }

            if (scrollMax > 0) {
                disableNext.value = false
            }
        })
    }, { immediate: true })
}

const loadedTiles = reactive(new Set<string>())
const mediaTiles = reactive(new Set<string>())

// Pre-populate only the first eager SSR tiles; remaining slots lazy-mount when visible.
// Must run during setup (not onMounted) so server and client hydration match.
// Guarded to server + hydration so client-side navigations still lazy-load via IntersectionObserver.
const { isHydrating } = useNuxtApp()
const eagerTiles = (import.meta.server || isHydrating) ? !!(props.games?.length) : false

// Eager-load the tiles visible by default: 8 per row, so a "double" list
// (inlineRows: 2) eager-loads 16. Per-list prop overrides the count outright.
const eagerTileCount = computed(() => props.eagerTileCount ?? 8 * Math.max(1, props.inlineRows ?? 1))

// Slugs of the first eager tiles, by game order — O(1) lookup per tile instead
// of a findIndex scan on every render.
const eagerSlugs = computed<Set<string>>(() => {
    if (!eagerTiles) return new Set()

    return new Set((props.games ?? []).slice(0, eagerTileCount.value).map(game => game.slug))
})

function isEagerTile(slug: string) {
    return eagerSlugs.value.has(slug)
}

if (eagerTiles) {
    for (const slug of eagerSlugs.value) {
        loadedTiles.add(slug)
        mediaTiles.add(slug)
    }
}

function onTileVisible([entry]: IntersectionObserverEntry[]) {
    if (!entry) return

    const slug = (entry.target as HTMLElement).dataset.tileSlug
    if (!slug) return

    if (entry.isIntersecting) {
        // Mount the tile once and keep it mounted, so scrolling back never
        // flashes a skeleton.
        loadedTiles.add(slug)
        // Mount the autoplay video only while the tile is near the viewport.
        mediaTiles.add(slug)
    }
    else {
        // Release the video element once the tile scrolls out of range — frees
        // its decode pipeline and memory. The tile and poster image stay mounted.
        mediaTiles.delete(slug)
    }
}

const listHasBeenScrolled = ref(false)

const handleScrollTracking = useDebounceFn((event: Event) => {
    const target = event.target as HTMLDivElement
    const { scrollWidth, clientWidth, scrollLeft } = target

    if (!listHasBeenScrolled.value) {
        trackEvent({
            event_name: `gamelist_scrolled`,
            event_type: 'Scroll',
            meta_data: [{
                category: 'Games',
                gamesCategory: props.category,
            }],
            log_entry: true
        }) as void

        listHasBeenScrolled.value = true
    }

    if (scrollLeft + clientWidth >= scrollWidth) {
        trackEvent({
            event_name: `gamelist_scrolled-to-end`,
            event_type: 'Scroll',
            meta_data: [{
                category: 'Games',
                gamesCategory: props.category,
                lastRow: !!target.previousElementSibling
            }],
            log_entry: true
        }) as void
    }
}, 200, { maxWait: 1000 })
</script>

<template>
    <div class="game-list__container">
        <template v-if="gameListRows.length">
            <div
                v-for="(gamesRow, index) in gameListRows"
                ref="gameListElement"
                :key="`${category}_${index}`"
                class="game-list"
                @scroll.passive="handleScrollTracking"
            >
                <div
                    v-for="(game, gameIndex) in gamesRow"
                    :key="`${category}_${game.slug}`"
                    v-intersection-observer="[onTileVisible, { rootMargin: '200px' }]"
                    :data-tile-slug="game.slug"
                    class="game-list__tile-slot"
                    :class="{ 'game-list__tile-slot--video': isVideoTile(game, props.videoEnabled) }"
                >
                    <GameTile
                        v-if="loadedTiles.has(game.slug)"
                        :game="game"
                        :video-enabled="props.videoEnabled"
                        :is-inline="true"
                        :is-mini="props.miniTiles"
                        :eager="isEagerTile(game.slug)"
                        :load-media="mediaTiles.has(game.slug)"
                        :style="{ '--game-index': gameIndex + index }"
                        @game-click="(game: Game) => emit('game-click', game)"
                    />

                    <div v-else class="skeleton-item" />
                </div>
            </div>
        </template>
        <template v-else-if="skeletonRows">
            <div v-for="row in skeletonRows" :key="`skeleton-row-${row}`" class="game-list">
                <div v-for="i in skeletons" :key="`skeleton_${row}_${i}`" class="skeleton-item" />
            </div>
        </template>
        <div
            class="game-list__prev"
            :class="{ 'game-list__prev--disabled': disablePrev }"
        >
            <button :disabled="disablePrev" @click.prevent="scrollGameList(false)">
                <UIIcon name="arrow-left" size="9" />
            </button>
        </div>
        <div
            class="game-list__next"
            :class="{ 'game-list__next--disabled': disableNext }"
        >
            <button :disabled="disableNext" @click.prevent="scrollGameList(true)">
                <UIIcon name="arrow-right" size="9" />
            </button>
        </div>
    </div>
</template>

<style lang="scss" scoped>
$controlBtnBg: $game-load-more-bg;
$controlBtnColor: $game-load-more-color;
$controlBtnBgHover: $game-load-more-bg-hover;
$controlBtnColorHover: $game-load-more-color-hover;

.game-list {
    position: relative;

    &__container {
        display: grid;
        gap: 10px;
    }

    &__prev,
    &__next {
        position: absolute;
        width: 80px;
        height: 100%;
        bottom: 0;
        left: 0;

        background: linear-gradient(to left, transparent 0%, $body-bg 85%, $body-bg 100%);
        z-index: 6;
        pointer-events: none;

        @include tablet {
            width: 100px;
        }

        @include desktop {
            width: 120px;
        }

        button {
            position: absolute;
            display: none;
            width: 32px;
            height: 50px;
            left: 0;
            bottom: 50%;
            margin: 0;
            border: none;
            padding: 0;
            transform: translateY(50%);

            align-items: center;
            justify-content: center;
            border-radius: var(--border-radius, 8px);
            color: $controlBtnColor;
            transition: all 0.4s $easeOutQuart;
            background: $controlBtnBg;
            opacity: 0.8;
            pointer-events: all;

            @include tablet {
                width: 40px;
                display: flex;
            }

            &:disabled {
                pointer-events: none;
                opacity: 0;
            }

            &:not([disabled]):hover {
                color: $controlBtnColorHover;
                background: $controlBtnBgHover;
                opacity: 1;
            }
        }

        &--disabled {
            opacity: 0;
        }
    }

    &__next {
        left: auto;
        right: 0;
        background: linear-gradient(to right, transparent 0%, $body-bg 100px, $body-bg 100%);

        button {
            left: auto;
            right: 15px;

            @include desktop {
                right: 0;
            }
        }
    }
}
</style>
